import { v4 as uuidv4 } from 'uuid';
import { indexedDBManager } from './IndexDBService';
import { Buffer } from 'buffer';
import RecordRTC from 'recordrtc';
import SentryService from './SentryService';

window.Buffer = Buffer;

interface MediaStreamRecordingOptions {
  getSharedMedia?: boolean;
  getTabMedia?: boolean;
  echoCancellation?: boolean;
  selectedMicrophoneId?: string;
  chunkLength?: number;
}

export interface MediaStreamInfo {
  streamType: 'shared' | 'microphone';
  selectedMicrophoneId?: string;
  tracks: {
    kind: string;
    label: string;
    enabled: boolean;
    muted: boolean;
    readyState: string;
  }[];
  constraints?: MediaTrackConstraints;
}

interface MediaStreamRecordingCallbacks {
  recorder?: RecorderCallbacks;
  audioStream?: AudioStreamCallbacks;
}

interface RecorderCallbacks {
  onChunkReady: (chunkId: string) => void;
  onChunkError: (chunkId: string, error: unknown) => void;
}

interface AudioStreamCallbacks {
  onAudioImpulse: (data: number) => void;
  onNoAudio: () => void;
  onSharedMediaError: () => void;
}

class WebRecordingService {
  public recorder?: RecordRTC;
  private audioContext?: AudioContext;
  private allStreams: MediaStream[] = [];
  private audioStream?: MediaStream;
  private currentChunkId: string | null = null;
  private recordingTimeout?: number | undefined;
  private recordingOptions?: MediaStreamRecordingOptions | undefined;
  private callbacks?: RecorderCallbacks | undefined;

  // interval ids for checking audio data
  private analyzeAudioIntervalId?: number;

  // Track service metrics
  private chunkStartTime: number = 0;
  private chunksProcessed: number = 0;
  private failedChunks: number = 0;
  private sessionId?: string;

  constructor() {
    indexedDBManager
      .initDB()
      .then(() => {
        console.log('Initialized DB');
      })
      .catch(error => {
        console.error('Failed to initialize IndexedDB:', error);
      });
  }

  public updateRecordingOption<K extends keyof MediaStreamRecordingOptions>(
    option: K,
    value: MediaStreamRecordingOptions[K],
  ): void {
    console.log(`UPDATE RECORDING OPTION: Changing recording option ${option} to ${value}.`);
    if (!this.recordingOptions) {
      this.recordingOptions = {};
    }
    this.recordingOptions[option] = value;

    // Add breadcrumb for recording options change
    SentryService.addBreadcrumb({
      category: 'audio.config',
      message: `Recording option updated: ${option}`,
      data: { option, value },
      level: 'info',
    });
  }

  private async createAudioStream(
    options: MediaStreamRecordingOptions,
    callbacks?: AudioStreamCallbacks,
  ) {
    // Create a span for audio stream creation
    const span = SentryService.startInactiveSpan('audio.stream', 'Create audio stream');

    let sharedMedia: MediaStream | undefined;
    let tabMedia: MediaStream | undefined;
    let micMedia: MediaStream | undefined;

    try {
      // if requested, get shared media
      if (options.getSharedMedia) {
        try {
          console.log('Requesting shared audio stream');
          SentryService.addBreadcrumb({
            category: 'audio.stream',
            message: 'Requesting shared audio stream',
            level: 'info',
          });

          sharedMedia = await navigator.mediaDevices.getDisplayMedia({
            // @ts-ignore
            preferCurrentTab: options.getTabMedia ? true : false,
            selfBrowserSurface: 'include',
            audio: true,
            video: true,
          });

          // Check if there's an audio track
          if (sharedMedia.getAudioTracks().length > 0) {
            // stop video track because not needed
            const videoTrack = sharedMedia.getVideoTracks()[0];
            videoTrack.stop();

            this.allStreams.push(sharedMedia);
          } else {
            console.log('No audio track in shared media, setting to undefined');
            callbacks?.onSharedMediaError();
            sharedMedia.getTracks().forEach(track => track.stop());

            SentryService.addBreadcrumb({
              category: 'audio.stream',
              message: 'No audio track in shared media',
              level: 'warning',
            });
          }
        } catch (e) {
          console.error("Couldn't get shared audio stream", e);

          SentryService.addBreadcrumb({
            category: 'audio.stream',
            message: 'Failed to get shared audio stream',
            level: 'error',
            data: { error: e instanceof Error ? e.message : String(e) },
          });
          // Instead of throwing, we'll just continue without shared media
        }
      }

      // get microphone media
      try {
        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Requesting microphone stream',
          data: { deviceId: options.selectedMicrophoneId },
          level: 'info',
        });

        micMedia = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: options.echoCancellation,
            deviceId: options.selectedMicrophoneId
              ? { exact: options.selectedMicrophoneId }
              : undefined,
          },
          video: false,
        });
        this.allStreams.push(micMedia);

        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Microphone stream acquired',
          data: {
            tracks: micMedia.getTracks().map(t => ({
              kind: t.kind,
              label: t.label,
            })),
          },
          level: 'info',
        });
      } catch (e) {
        console.error("Couldn't get mic audio stream", e);
        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Failed to get microphone stream',
          level: 'error',
          data: { error: e instanceof Error ? e.message : String(e) },
        });

        span?.setStatus({
          code: 2,
          message: e instanceof Error ? e.message : String(e),
        });
        throw e; // Re-throw this error as we can't proceed without mic audio
      }

      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }

      let audioStream: MediaStream | undefined;

      // if just mic
      if (micMedia && !sharedMedia && !tabMedia) {
        audioStream = micMedia;
      }

      // if mic and shared media
      if (micMedia && sharedMedia) {
        const sharedSource = this.audioContext.createMediaStreamSource(sharedMedia);
        const micSource = this.audioContext.createMediaStreamSource(micMedia);

        const combinedDestination = this.audioContext.createMediaStreamDestination();

        sharedSource.connect(combinedDestination);
        micSource.connect(combinedDestination);

        audioStream = combinedDestination.stream;
        this.allStreams.push(audioStream);

        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Created combined audio stream (microphone + shared media)',
          level: 'info',
        });
      }

      // if mic and tab media
      if (micMedia && tabMedia) {
        const tabSource = this.audioContext.createMediaStreamSource(tabMedia);
        const micSource = this.audioContext.createMediaStreamSource(micMedia);

        const combinedDestination = this.audioContext.createMediaStreamDestination();

        tabSource.connect(combinedDestination);
        micSource.connect(combinedDestination);

        audioStream = combinedDestination.stream;
        this.allStreams.push(audioStream);

        SentryService.addBreadcrumb({
          category: 'audio.stream',
          message: 'Created combined audio stream (microphone + tab media)',
          level: 'info',
        });
      }

      this.audioStream = audioStream;

      // send audio level data for visualizer and check for no audio
      if (audioStream) {
        const source = this.audioContext.createMediaStreamSource(audioStream);
        const analyzer = this.audioContext.createAnalyser();
        analyzer.fftSize = 32; // Reduce FFT size for efficiency
        analyzer.smoothingTimeConstant = 0; // Disable smoothing for instant response (prevent long decay)
        const bufferLength = analyzer.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        source.connect(analyzer);

        let lastAudioTime = Date.now();
        const silenceThreshold = 0.01; // very low
        const silencePeriod = 3000; // 3 seconds
        let silenceWarningCount = 0;

        const analyzeAudio = () => {
          analyzer.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

          if (callbacks?.onAudioImpulse) {
            callbacks.onAudioImpulse(average);
          }

          if (average > silenceThreshold) {
            lastAudioTime = Date.now();
          } else if (Date.now() - lastAudioTime > silencePeriod) {
            if (callbacks?.onNoAudio) {
              callbacks.onNoAudio();
              silenceWarningCount++;

              // Only log to Sentry every 3rd silence warning to avoid noise
              if (silenceWarningCount % 3 === 0) {
                SentryService.addBreadcrumb({
                  category: 'audio.quality',
                  message: 'No audio detected for extended period',
                  level: 'warning',
                  data: { silenceDuration: Date.now() - lastAudioTime },
                });
              }
            }
            lastAudioTime = Date.now();
          }
        };

        // Use setInterval to control the update frequency
        const intervalId = setInterval(analyzeAudio, 50) as unknown as number; // Update every 50ms for responsive visualization

        // Store the interval ID to clear it later if needed
        this.analyzeAudioIntervalId = intervalId;
      }

      span?.setStatus({ code: 1 });
      span?.setAttribute(
        'streamConfig',
        JSON.stringify({
          hasMicrophoneStream: !!micMedia,
          hasSharedMedia: !!sharedMedia,
          hasTabMedia: !!tabMedia,
          hasCombinedStream: !!audioStream && (!!sharedMedia || !!tabMedia),
          tracks: audioStream?.getTracks().length || 0,
        }),
      );
    } catch (error) {
      console.error(`Error creating audio stream: ${error}`);
      SentryService.logEvent('Error creating audio stream.', {
        level: 'error',
        extra: { error },
      });

      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));
      throw error; // Re-throw the error to be handled by the caller
    } finally {
      span?.end();
    }
  }

  private getSupportedMimeType(): string | undefined {
    const types = [
      'audio/webm;codecs=opus', // Preferred type for modern browsers
      'audio/webm', // Fallback to basic WebM if Opus is not supported
      'audio/mp4', // Mobile
      'audio/ogg', // Who knows
    ];

    return types.find(type => MediaRecorder.isTypeSupported(type));
  }

  private async createMediaRecorder(
    options: MediaStreamRecordingOptions,
    callbacks?: RecorderCallbacks,
  ) {
    SentryService.startSpan('audio.recorder', 'Create media recorder', span => {
      if (!this.audioStream) {
        console.error('Cannot create a RecordRTC instance without a defined audio stream.');
        span.setStatus({ code: 2, message: 'No audio stream available' });
        span.setAttribute('error', 'No audio stream available');
        span.end();
        return;
      }

      this.recordingOptions = options;
      this.callbacks = callbacks;

      SentryService.addBreadcrumb({
        category: 'audio.recorder',
        message: 'Starting new chunk recording',
        level: 'info',
        data: {
          chunkLength: this.recordingOptions?.chunkLength,
          mimeType: this.getSupportedMimeType(),
        },
      });

      span.setStatus({ code: 1 });
      span.end();

      this.startNewChunk();
    });
  }

  private async startNewChunk() {
    if (!this.audioStream) {
      console.error('No audio stream available');

      SentryService.addBreadcrumb({
        category: 'audio.recorder',
        message: 'Failed to start new chunk - no audio stream',
        level: 'error',
      });
      return;
    }

    if (this.audioStream.active === false) {
      console.error('Audio stream is not active');

      SentryService.addBreadcrumb({
        category: 'audio.recorder',
        message: 'Failed to start new chunk - audio stream not active',
        level: 'error',
      });
      return;
    }

    const mimeType = this.getSupportedMimeType();
    const chunkId = uuidv4();
    this.currentChunkId = chunkId;
    this.chunkStartTime = performance.now();

    console.log(`Starting new chunk: ${chunkId}`);
    const span = SentryService.startInactiveSpan('audio.chunk', `Record chunk ${chunkId}`);

    try {
      span?.setAttribute('chunkId', chunkId);

      const newRecorder = new RecordRTC(this.audioStream, {
        type: 'audio',
        mimeType: mimeType as unknown as any,
        recorderType: RecordRTC.MediaStreamRecorder,
      });

      this.recorder = newRecorder;

      newRecorder.startRecording();
      console.log(`Recording started for chunk ${chunkId}`);

      SentryService.addBreadcrumb({
        category: 'audio.chunk',
        message: `Started recording chunk: ${chunkId}`,
        level: 'info',
        data: { mimeType },
      });

      // Schedule the next chunk
      this.recordingTimeout = setTimeout(async () => {
        span?.setAttribute('duration', performance.now() - this.chunkStartTime);

        this.stopAndProcessCurrentChunk(newRecorder, chunkId);
        this.startNewChunk();
      }, this.recordingOptions?.chunkLength) as unknown as number;
    } catch (error) {
      console.error(`Error starting new chunk ${chunkId}:`, error);
      SentryService.logEvent('Error starting new chunk.', {
        level: 'error',
        extra: { error, chunkId },
      });

      SentryService.addBreadcrumb({
        category: 'audio.chunk',
        message: `Failed to start chunk: ${chunkId}`,
        level: 'error',
        data: { error: error instanceof Error ? error.message : String(error) },
      });
    } finally {
      span?.end();
    }
  }

  private async stopAndProcessCurrentChunk(recorder: RecordRTC, chunkId: string): Promise<void> {
    const span = SentryService.startInactiveSpan('audio.chunk.process', `Process chunk ${chunkId}`);
    span?.setAttribute('chunkId', chunkId);

    const work = new Promise<void>(resolve => {
      if (recorder) {
        console.log(`Stopping chunk ${chunkId}`);

        try {
          const state = recorder.getState();
          console.log(`Recorder state before stopping: ${state}`);

          if (state !== 'recording') {
            console.warn(`Recorder is not in 'recording' state. Current state: ${state}`);

            SentryService.addBreadcrumb({
              category: 'audio.chunk.process',
              message: `Cannot stop chunk - recorder in wrong state: ${state}`,
              level: 'warning',
              data: { chunkId, state },
            });

            span?.setStatus({ code: 2 });
            span?.setAttribute('error', `Recorder not in recording state (${state})`);
            return resolve();
          }

          recorder.stopRecording(async () => {
            console.log(`Chunk ${chunkId} stopped`);

            const chunkDuration = performance.now() - this.chunkStartTime;
            SentryService.addBreadcrumb({
              category: 'audio.chunk',
              message: `Stopped chunk: ${chunkId}`,
              level: 'info',
              data: { durationMs: chunkDuration },
            });

            // Track chunk metrics in the transaction
            SentryService.setMeasurement(
              `chunk_duration_${this.chunksProcessed}`,
              chunkDuration,
              'millisecond',
            );

            try {
              // Give a little time for the recorder to finalize the blob
              await new Promise(r => setTimeout(r, 800));

              const blob = recorder.getBlob();
              // console.log(`Blob for chunk ${chunkId}:`, blob?.size);

              if (blob && blob.size > 0) {
                // console.log(`Processing blob for chunk ${chunkId}`);
                await this.processChunk(blob, chunkId);
                this.chunksProcessed++;
              } else {
                // Enhanced error reporting
                const diagnosticInfo = {
                  chunkId,
                  blobExists: !!blob,
                  blobSize: blob ? blob.size : 0,
                  recorderState: recorder.getState(),
                  recorderOptions: (recorder.getInternalRecorder() as any)?.options || {},
                  audioStreamActive: this.audioStream ? this.audioStream.active : false,
                  audioStreamTracks: this.audioStream
                    ? this.audioStream.getTracks().map(track => ({
                        kind: track.kind,
                        label: track.label,
                        enabled: track.enabled,
                        muted: track.muted,
                        readyState: track.readyState,
                        constraints: track.getConstraints(),
                      }))
                    : [],
                  recordingOptions: this.recordingOptions,
                  chunkLength: this.recordingOptions?.chunkLength,
                  browserInfo: {
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    mediaDevices: !!navigator.mediaDevices,
                  },
                  memoryInfo:
                    // @ts-ignore - Chrome-specific performance memory API
                    performance && performance.memory
                      ? {
                          // @ts-ignore - Chrome-specific performance memory API
                          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
                          // @ts-ignore - Chrome-specific performance memory API
                          totalJSHeapSize: performance.memory.totalJSHeapSize,
                          // @ts-ignore - Chrome-specific performance memory API
                          usedJSHeapSize: performance.memory.usedJSHeapSize,
                        }
                      : 'Not available',
                };

                console.error(`No valid blob for chunk ${chunkId}`, diagnosticInfo);

                // Create a custom error with all the diagnostic info
                const blobError = new Error(`No valid blob for chunk ${chunkId}`);

                // Set specific tags for these errors to easily filter in Sentry
                SentryService.setTag('error_type', 'blob_chunk_error');
                SentryService.setTag('chunk_id', chunkId);
                SentryService.setTag('requires_replay', 'true');

                // Capture the exception with high priority to ensure replay capture
                SentryService.captureException(blobError, {
                  level: 'error',
                  tags: {
                    is_critical: 'true',
                  },
                  extra: diagnosticInfo,
                });

                // Add a console log that will be captured as a breadcrumb in Sentry
                console.warn('CRITICAL ERROR: Recording chunk failed to produce valid blob');

                this.failedChunks++;
                span?.setStatus({ code: 2 });
                span?.setAttribute('error', 'No valid blob produced');
                span?.setAttribute('diagnostics', JSON.stringify(diagnosticInfo));

                throw blobError;
              }
            } catch (error) {
              console.error(`Error processing chunk ${chunkId}:`, error);
              SentryService.logEvent('Error processing chunk.', {
                level: 'error',
                extra: { chunkId, error },
              });
              if (this.callbacks?.onChunkError) {
                this.callbacks.onChunkError(chunkId, error);
              }

              span?.setStatus({ code: 2 });
              span?.setAttribute('error', error instanceof Error ? error.message : String(error));
            } finally {
              resolve();
            }
          });
        } catch (error) {
          console.error(`Error stopping recorder for chunk ${chunkId}:`, error);
          SentryService.logEvent('Error stopping recorder for chunk.', {
            level: 'error',
            extra: { chunkId, error },
          });

          span?.setStatus({ code: 2 });
          span?.setAttribute('error', error instanceof Error ? error.message : String(error));

          resolve();
        }
      } else {
        console.warn('No recorder to stop');

        SentryService.addBreadcrumb({
          category: 'audio.chunk',
          message: 'No recorder to stop',
          level: 'warning',
        });

        span?.setStatus({ code: 2 });
        span?.setAttribute('error', 'No recorder to stop');

        resolve();
      }
    });

    return work.finally(() => {
      span?.end();
    });
  }

  private async processChunk(blob: Blob, chunkId: string) {
    const span = SentryService.startInactiveSpan('audio.chunk.save', `Save chunk ${chunkId}`);

    try {
      console.log(`Processing chunk ${chunkId}`);

      span?.setAttribute('chunkId', chunkId);
      span?.setAttribute('blobSize', blob.size);
      span?.setAttribute('blobType', blob.type);

      SentryService.addBreadcrumb({
        category: 'audio.chunk',
        message: `Processing chunk: ${chunkId}`,
        level: 'info',
        data: { size: blob.size, type: blob.type },
      });

      const startTime = performance.now();
      await indexedDBManager.writeBlob(blob, chunkId);
      const duration = performance.now() - startTime;

      span?.setAttribute('duration', duration);
      SentryService.setMeasurement('indexeddb_write', duration, 'millisecond');

      SentryService.addBreadcrumb({
        category: 'audio.chunk',
        message: `Saved chunk ${chunkId} to IndexedDB`,
        level: 'info',
        data: { durationMs: duration },
      });

      if (this.callbacks?.onChunkReady) {
        this.callbacks.onChunkReady(chunkId);
        span?.setAttribute('callbackFired', true);
      }

      span?.setStatus({ code: 1 });
    } catch (error) {
      console.error(`Process chunk error: ${error}`);

      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      if (this.callbacks?.onChunkError) {
        this.callbacks.onChunkError(chunkId, error);
      }
    } finally {
      span?.end();
    }
  }

  public getCurrentChunkId(): string | null {
    return this.currentChunkId;
  }

  public pauseRecording() {
    if (this.recorder && this.currentChunkId && this.recorder.getState() === 'recording') {
      console.log('Pausing recording...');

      SentryService.addBreadcrumb({
        category: 'audio.recording',
        message: 'Pausing recording',
        level: 'info',
      });

      clearTimeout(this.recordingTimeout);
      this.stopAndProcessCurrentChunk(this.recorder, this.currentChunkId);
    }
  }

  public resumeRecording() {
    if (this.recorder && this.recorder.getState() === 'stopped') {
      console.log('Resuming recording...');

      SentryService.addBreadcrumb({
        category: 'audio.recording',
        message: 'Resuming recording',
        level: 'info',
      });

      this.startNewChunk();
    }
  }

  public async stopMediaStreamRecording(shortRecording?: boolean) {
    const span = SentryService.startInactiveSpan('audio.recording.stop', 'Stop recording');
    span?.setAttribute('shortRecording', !!shortRecording);

    try {
      SentryService.addBreadcrumb({
        category: 'audio.recording',
        message: 'Stopping media stream recording',
        level: 'info',
        data: {
          shortRecording: !!shortRecording,
          chunksProcessed: this.chunksProcessed,
          failedChunks: this.failedChunks,
        },
      });

      // end recorder
      if (this.recorder && this.currentChunkId && !shortRecording) {
        await this.stopAndProcessCurrentChunk(this.recorder, this.currentChunkId);
      }

      // clear recording timeout
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
        this.recordingTimeout = undefined;
      }

      // clear analysis interval
      if (this.analyzeAudioIntervalId) {
        clearInterval(this.analyzeAudioIntervalId);
        this.analyzeAudioIntervalId = undefined;
      }

      // stop all media streams
      for (const stream of this.allStreams) {
        const tracks = stream.getTracks();
        for (const track of tracks) {
          console.log('Stopping track', track);
          track.stop();
        }
      }

      // remove audioStreams and recorder and audioContext
      this.allStreams = [];
      this.audioStream = undefined;
      this.recorder = undefined;
      this.audioContext = undefined;

      // Add recording metrics to transaction
      SentryService.setMeasurement('total_chunks', this.chunksProcessed, 'count');
      SentryService.setMeasurement('failed_chunks', this.failedChunks, 'count');

      // Reset chunk counters
      this.chunksProcessed = 0;
      this.failedChunks = 0;

      span?.setStatus({ code: 1 });
    } catch (error) {
      console.error(`Error stopping media stream recording: ${error}`);
      SentryService.logEvent('Error stopping media stream recording.', {
        level: 'error',
        extra: { error },
      });

      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));
    } finally {
      span?.end();
    }
  }

  public async startMediaRecorder(
    {
      getSharedMedia = false,
      getTabMedia = false,
      echoCancellation = false,
      selectedMicrophoneId = undefined,
      chunkLength = 90000,
    }: MediaStreamRecordingOptions = {},
    callbacks: MediaStreamRecordingCallbacks,
  ): Promise<boolean> {
    const options = {
      getSharedMedia,
      getTabMedia,
      echoCancellation,
      selectedMicrophoneId,
      chunkLength,
    };

    const span = SentryService.startInactiveSpan('audio.recording.start', 'Start media recorder');

    try {
      // Generate a unique ID for this recording session if not set
      if (!this.sessionId) {
        this.sessionId = uuidv4();

        // Set attributes directly on the span instead of using custom method
        span?.setAttribute('recordingId', this.sessionId);

        // Add a breadcrumb to mark the start of recording session
        SentryService.addBreadcrumb({
          type: 'recording',
          category: 'audio.recording',
          message: 'Recording session started',
          level: 'info',
          data: { recordingId: this.sessionId },
        });

        // Add a breadcrumb to mark session replay point instead of using custom method
        SentryService.addBreadcrumb({
          type: 'user',
          category: 'replay',
          message: 'Session replay starting point',
          level: 'info',
        });
      }

      span?.setAttribute('selectedMicrophoneId', selectedMicrophoneId || 'default');
      span?.setAttribute('useSharedMedia', String(getSharedMedia));
      span?.setAttribute('chunkLength', String(chunkLength));

      SentryService.addBreadcrumb({
        category: 'audio.recording',
        message: 'Starting media recorder',
        level: 'info',
        data: options,
      });

      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }

      // if an audioStream hasn't already been started, start one
      if (!this.audioStream) {
        await this.createAudioStream(options, callbacks?.audioStream);
      }

      if (!this.audioStream) {
        throw new Error('No audio stream created.');
      }

      if (!this.recorder) {
        await this.createMediaRecorder(options, callbacks?.recorder);
      }

      if (!this.recorder) {
        throw new Error('No recorder created.');
      }

      return true;
    } catch (error) {
      console.error(`Error creating media stream recorder: ${error}`);
      SentryService.logEvent('Error creating media stream recorder.', {
        level: 'error',
        extra: { error },
      });
      return false;
    } finally {
      span?.end();
    }
  }

  public getMediaStreamInfo(): MediaStreamInfo | null {
    if (!this.audioStream) {
      return null;
    }

    return {
      streamType: this.recordingOptions?.getSharedMedia ? 'shared' : 'microphone',
      selectedMicrophoneId: this.recordingOptions?.selectedMicrophoneId,
      tracks: this.audioStream.getTracks().map(track => ({
        kind: track.kind,
        label: track.label,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      })),
      constraints: this.audioStream.getAudioTracks()[0]?.getConstraints(),
    };
  }

  // Mic test mode - only monitors audio levels without recording
  public async startMicTest(
    selectedMicrophoneId: string | undefined,
    callbacks: { onAudioImpulse: (level: number) => void },
  ): Promise<boolean> {
    try {
      console.log('Starting mic test mode...');

      // Get microphone stream
      const constraints: MediaStreamConstraints = {
        audio: selectedMicrophoneId ? { deviceId: { exact: selectedMicrophoneId } } : true,
        video: false,
      };

      const micStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Set up audio analysis
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }

      const source = this.audioContext.createMediaStreamSource(micStream);
      const analyzer = this.audioContext.createAnalyser();
      analyzer.fftSize = 256;
      analyzer.smoothingTimeConstant = 0; // Disable smoothing for instant response (prevent long decay)
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      source.connect(analyzer);

      // Analysis function
      const analyzeAudio = () => {
        analyzer.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        callbacks.onAudioImpulse(average);
      };

      // Start analyzing
      this.analyzeAudioIntervalId = setInterval(analyzeAudio, 50) as unknown as number;

      // Store the stream so we can stop it later
      this.allStreams.push(micStream);

      return true;
    } catch (error) {
      console.error('Error starting mic test:', error);
      return false;
    }
  }

  // Stop mic test mode
  public stopMicTest() {
    console.log('Stopping mic test mode...');

    // Clear the analysis interval
    if (this.analyzeAudioIntervalId) {
      clearInterval(this.analyzeAudioIntervalId);
      this.analyzeAudioIntervalId = undefined;
    }

    // Stop all streams
    this.allStreams.forEach(stream => {
      stream.getTracks().forEach(track => track.stop());
    });
    this.allStreams = [];

    // Close audio context if no longer needed
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = undefined;
    }
  }
}

export default WebRecordingService;
